/**
 * Data-driven social network registry (Organization / Creator / Project / Donate).
 * Keep in sync with backend App\Support\SocialNetworkRegistry.
 */

export const SOCIAL_NETWORKS = {
  facebook: {
    label: "Facebook",
    builtin_icon: "fab fa-facebook-f",
    css_class: "facebook",
  },
  instagram: {
    label: "Instagram",
    builtin_icon: "fab fa-instagram",
    css_class: "instagram",
  },
  youtube: {
    label: "YouTube",
    builtin_icon: "fab fa-youtube",
    css_class: "youtube",
  },
  linkedin: {
    label: "LinkedIn",
    builtin_icon: "fab fa-linkedin-in",
    css_class: "linkedin",
  },
  tiktok: {
    label: "TikTok",
    builtin_icon: "fab fa-tiktok",
    css_class: "tiktok",
  },
  x: {
    label: "X",
    builtin_icon: "fab fa-x-twitter",
    css_class: "x",
    aliases: ["twitter"],
    // Older FA packs may lack fa-x-twitter
    fallback_icon: "fab fa-twitter",
  },
  threads: {
    label: "Threads",
    builtin_icon: "fab fa-threads",
    css_class: "threads",
    fallback_icon: "fas fa-at",
  },
  bluesky: {
    label: "Bluesky",
    builtin_icon: "fas fa-cloud",
    css_class: "bluesky",
    aliases: ["bsky"],
  },
  website: {
    label: "Website",
    builtin_icon: "fas fa-globe",
    css_class: "website",
  },
};

export function normalizeSocialNetworkKey(raw) {
  const key = String(raw || "")
    .trim()
    .toLowerCase();
  if (!key) return "";
  if (SOCIAL_NETWORKS[key]) return key;
  const entries = Object.keys(SOCIAL_NETWORKS);
  for (let i = 0; i < entries.length; i += 1) {
    const canonical = entries[i];
    const meta = SOCIAL_NETWORKS[canonical];
    const aliases = meta.aliases || [];
    if (aliases.indexOf(key) !== -1) return canonical;
    if (String(meta.label).toLowerCase() === key) return canonical;
  }
  return key;
}

export function socialNetworkMeta(network) {
  const key = normalizeSocialNetworkKey(network);
  if (SOCIAL_NETWORKS[key]) {
    return { key, ...SOCIAL_NETWORKS[key] };
  }
  return {
    key,
    label: key ? key.charAt(0).toUpperCase() + key.slice(1) : "Link",
    builtin_icon: "fas fa-link",
    css_class: key || "link",
  };
}

/**
 * Icon URL priority for a link object:
 * 1) uploaded custom icon_url / paths
 * 2) Brand Link-shaped custom_icon.*
 * 3) null → caller uses builtin / fallback
 */
export function extractCustomIconUrl(link) {
  if (!link || typeof link !== "object") return null;
  const direct = [
    link.icon_url,
    link.icon_path,
    link.custom_icon_path,
  ];
  for (let i = 0; i < direct.length; i += 1) {
    const value = direct[i];
    if (value && String(value).trim()) {
      return String(value).trim();
    }
  }
  const custom = link.custom_icon;
  if (custom && typeof custom === "object") {
    const sizes = ["md", "lg", "sm", "original"];
    for (let i = 0; i < sizes.length; i += 1) {
      const value = custom[sizes[i]];
      if (value && String(value).trim()) {
        return String(value).trim();
      }
    }
  }
  return null;
}

export function resolveBuiltinSocialIcon(linkOrNetwork) {
  if (linkOrNetwork && typeof linkOrNetwork === "object") {
    if (linkOrNetwork.builtin_icon) {
      return String(linkOrNetwork.builtin_icon).trim();
    }
    const meta = socialNetworkMeta(
      linkOrNetwork.css_class || linkOrNetwork.network || linkOrNetwork.label
    );
    return meta.builtin_icon || meta.fallback_icon || "fas fa-link";
  }
  const meta = socialNetworkMeta(linkOrNetwork);
  return meta.builtin_icon || meta.fallback_icon || "fas fa-link";
}
